import {
  useInfiniteQuery,
  UseInfiniteQueryResult,
} from "@tanstack/react-query";

import { DocumentFilters, InvoiceModel } from "api";
import { useDocumentService } from "contexts/ServiceContext";
import useSelectedCustomerCode from "hooks/useSelectedCustomerCode";
import { formatNumber, valueOr, valueToDate } from "util/statHelpers";
import { AI_EVENTS, useTrackEvent } from "../../useAppInsight";
import useListOfTransportationModes from "../mode-of-transportation/useListOfTransportationModes";
import { getProductName } from "util/getProductName";

const mapApiDocumentInvoiceValues = (
  invoice: InvoiceModel
): DocumentsInvoices => ({
  docEntry: invoice.DocEntry ?? 0,
  invoiceNumber: valueOr(invoice.DocumentNumber),
  releaseNumber: valueOr(invoice.ReleaseNumber),
  contractNumber: invoice.ContractNumber,
  dueDate: valueToDate(invoice.DocDueDate),
  poNumber: valueOr(invoice.NumAtCard),
  invoiceDate: valueToDate(invoice.DocDate),
  amountDue: invoice.CalculatedAmountDue ?? 0,
  productName: getProductName(invoice.DocumentLines),
  amountDueFormatted: valueOr(
    invoice.CalculatedAmountDue !== undefined
      ? formatNumber(invoice.CalculatedAmountDue)
      : undefined
  ),
  currency: valueOr(invoice.Currency),
  shipDate: valueToDate(invoice.DeliveryDate),
  shipTo: invoice.ShipToCode,
  mode: invoice.ModeOfTransportationCode,
  salesTerms: invoice.SalesTermsCode,
  isCredit: invoice.ObjectType === "oCreditNotes",
});

const mapApiDocumentInvoiceFilters = (
  filters?: DocumentsInvoicesFilters
): DocumentFilters => {
  return {
    CardCodes: filters?.cardCodes,
    DocDateEnd: filters?.invoiceDateEndDate,
    DocDateStart: filters?.invoiceDateStartDate,
    DocDueDateEnd: filters?.dueDateEndDate,
    DocDueDateStart: filters?.dueDateStartDate,
    Modes: filters?.mode,
    ProductCodes: filters?.product,
    SalesTerms: filters?.salesTerms,
    SearchString: filters?.searchString,
    ShipDateEnd: filters?.shipDateEndDate,
    ShipDateStart: filters?.shipDateStartDate,
    ShipToCodes: filters?.shipTo,
  };
};

const useDocumentInvoices = (
  currentSort?: { key: keyof InvoiceModel; direction: string },
  requestBody?: DocumentsInvoicesFilters
): UseInfiniteQueryResult<{
  invoices: DocumentsInvoices[];
}> => {
  const cardCodes = useSelectedCustomerCode();
  const documentService = useDocumentService();
  const trackQuery = useTrackEvent(AI_EVENTS.API_REQUEST, {
    cardCodes,
  });
  const trackQueryError = useTrackEvent(AI_EVENTS.API_ERROR, {
    cardCodes,
  });

  const { data: modes, isLoading } = useListOfTransportationModes();

  return useInfiniteQuery({
    queryKey: ["documentInvoices", { currentSort, requestBody }],
    queryFn: ({ pageParam = 0 }) => {
      const filterParams = {
        sort: currentSort,
        page: pageParam,
      };
      const eventId = trackQuery({
        endpoint: "Invoice/SearchNFilterGridView",
        parameters: filterParams,
      });
      return documentService
        .getDocumentInvoicesList(
          filterParams,
          mapApiDocumentInvoiceFilters(requestBody)
        )
        .then((data) => ({
          ...data,
          value: data.value?.map((value) => ({
            ...value,
            ModeOfTransportationCode:
              modes?.find(
                (mode) => mode.code === value.ModeOfTransportationCode
              )?.name ?? value.ModeOfTransportationCode,
          })),
        }))
        .then(({ value, ...rest }) => ({
          ...rest,
          invoices: value?.map(mapApiDocumentInvoiceValues) ?? [],
        }))
        .catch((err) => {
          trackQueryError(
            {
              statusCode: err.statusCode,
              message: err.message,
            },
            eventId
          );
          throw err;
        });
    },
    enabled: !isLoading,
  });
};

export default useDocumentInvoices;
